/** @format */

import { Reducer } from "react";
import { ResourceLink } from "../../../types";
import { getUniqueCategories } from "../../../utils/functions";

export const DEFAULT_CATEGORY_NAME = "None";

export interface LinksState {
  links: ResourceLink[];
  categories: string[];
}

export const initialLinksState: LinksState = {
  links: [],
  categories: [DEFAULT_CATEGORY_NAME],
};

export type LinksAction =
  | { type: "freshLinks"; payload: { newLinks: ResourceLink[] } }
  //Link actions
  | {
      type: "updateLinkTitle";
      payload: { linkIndex: number; newTitle: string };
    }
  | { type: "updateLinkSrc"; payload: { linkIndex: number; newSrc: string } }
  | { type: "newLink"; payload: { linkIndex: number; category: string } }
  | { type: "deleteLink"; payload: { linkIndex: number } }
  //Category actions
  | {
      type: "updateCategory";
      payload: { categoryIndex: number; newCategoryName: string };
    }
  | { type: "newCategory" }
  | { type: "deleteCategory"; payload: { categoryIndex: number } };

const getMimeType = (extension: string): string => {
  switch (extension) {
    case "pdf":
      return "pdf";
    case "com":
      return "webpage";
    case "ppt":
    case "pptx":
      return "mspowerpoint";
    case "doc":
    case "docx":
      return "msword";
    case "xlsx":
    case "xlsm":
    case "xlsb":
      return "msexcel";
    //TODO: Not sure what these should be
    // case "mp4":
    //   return "video";
    // case "print":
    //   return "print";
    default:
      return "";
  }
};

/**
 * @returns the number of links before the first link in the specified category
 */
export const getLinkOffset = (catIndex: number, state: LinksState): number => {
  let categoryStartIndex = 0;
  for (let i = 0; i < catIndex; i++) {
    categoryStartIndex += state.links.filter(
      (link) => link.category === state.categories[i],
    ).length;
  }
  return categoryStartIndex;
};

/**
 * replaces an element in an array (pure)
 * @returns the new array
 */
const pureAddArrayElement = <T>(
  newElement: T,
  oldArray: T[],
  newIndex: number,
): any[] => {
  if (newIndex === 0) {
    return [newElement, ...oldArray.slice(1)];
  }

  return [
    ...oldArray.slice(0, newIndex),
    newElement,
    ...oldArray.slice(newIndex),
  ];
};

/**
 * mutates an element in an array (pure)
 * @returns the new array with mutated element
 */
const pureUpdateArrayElement = <T>(
  newElement: T,
  oldArray: T[],
  updateElementIndex: number,
): any[] => {
  if (updateElementIndex === 0) {
    return [newElement, ...oldArray.slice(1)];
  }

  return [
    ...oldArray.slice(0, updateElementIndex),
    newElement,
    ...oldArray.slice(updateElementIndex + 1),
  ];
};

//TODO: some more validation could be added to the reducer?
export const ResourceLinksReducer: Reducer<LinksState, LinksAction> = (
  state: LinksState,
  action: LinksAction,
): LinksState => {
  // Category actions
  if (action.type === "newCategory") {
    return { links: state.links, categories: [...state.categories, ""] };
  }

  if (action.type === "deleteCategory") {
    if (
      state.categories[action.payload.categoryIndex] ===
        DEFAULT_CATEGORY_NAME &&
      action.payload.categoryIndex === 0
    )
      return state; // Can't delete none

    let deletedCats = [
      ...state.categories.slice(0, action.payload.categoryIndex),
      ...state.categories.slice(action.payload.categoryIndex + 1),
    ]; // NOTE: slice is used to as it is pure

    // also delete all the links the category contains
    let deletedLinks = state.links.filter(
      (link) =>
        link.category !== state.categories[action.payload.categoryIndex],
    );
    return { links: deletedLinks, categories: deletedCats };
  }

  if (action.type === "updateCategory") {
    if (
      (state.categories[action.payload.categoryIndex] !==
        DEFAULT_CATEGORY_NAME &&
        action.payload.categoryIndex === 0) || // Can't change none
      state.categories.indexOf(action.payload.newCategoryName) !== -1 // Can't use the same name
    )
      return state;
    // Move all the links that are in this category to the newly named category
    const oldCatName = state.categories[action.payload.categoryIndex];

    const newLinks = state.links.map((link) => {
      if (link.category === oldCatName) {
        return { ...link, category: action.payload.newCategoryName };
      }
      return link;
    });

    let newCats = pureUpdateArrayElement(
      action.payload.newCategoryName,
      state.categories,
      action.payload.categoryIndex,
    );

    return { links: newLinks, categories: newCats };
  }

  // Links actions
  if (action.type === "newLink") {
    // find the last occurence of this category
    // add new link there
    const lastIndex =
      action.payload.linkIndex +
      state.links.filter((link) => link.category === action.payload.category)
        .length;

    const emptyLink: ResourceLink = {
      category: action.payload.category,
      added: "",
      label: "",
      mimeType: "",
      order: 0,
      src: "",
    };

    return {
      links: pureAddArrayElement(emptyLink, state.links, lastIndex),
      categories: state.categories,
    };
  }

  if (action.type === "deleteLink") {
    return {
      links: [
        ...state.links.slice(0, action.payload.linkIndex),
        ...state.links.slice(action.payload.linkIndex + 1),
      ],
      categories: state.categories,
    };
  }

  if (action.type === "updateLinkSrc") {
    let newLink = {
      ...state.links[action.payload.linkIndex],
      src: action.payload.newSrc,
      mimeType: getMimeType(
        action.payload.newSrc.substring(
          action.payload.newSrc.lastIndexOf(".") + 1,
        ),
      ),
    };

    return {
      categories: state.categories,
      links: pureUpdateArrayElement(
        newLink,
        state.links,
        action.payload.linkIndex,
      ),
    };
  }

  if (action.type === "updateLinkTitle") {
    let newLink = {
      ...state.links[action.payload.linkIndex],
      label: action.payload.newTitle,
    };

    return {
      categories: state.categories,
      links: pureUpdateArrayElement(
        newLink,
        state.links,
        action.payload.linkIndex,
      ),
    };
  }

  if (action.type === "freshLinks") {
    if (!action.payload.newLinks) {
      console.error("new links undefined");
      return state;
    }

    // Also set the categories based on the new links
    let newCats = getUniqueCategories(action.payload.newLinks);
    if (!newCats.includes(DEFAULT_CATEGORY_NAME))
      newCats = [DEFAULT_CATEGORY_NAME, ...newCats];

    return { categories: newCats, links: action.payload.newLinks };
  }

  return state;
};
